/**
 * RolesTab Component
 *
 * Main component for the RBAC roles management UI.
 * Connected to real backend API via useRoleManagement hooks.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { RolesList } from './RolesList';
import { RoleFormDialog, type RoleFormData } from './RoleFormDialog';
import { useCreateRole } from '@/hooks';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { describeApiError } from '@/lib/describeApiError';

const MANAGE_ROLES_PERMISSION = 'settings.manage_roles';

export function RolesTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Creating a role requires settings.manage_roles server-side; hide what would 403.
  const canManageRoles = hasPermission(MANAGE_ROLES_PERMISSION);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const createRole = useCreateRole();

  const handleCreateRole = (formData: RoleFormData) => {
    if (!hasPermission(MANAGE_ROLES_PERMISSION)) return;
    createRole.mutate(
      {
        name: formData.name,
        description: formData.description,
        // Workshop UI does not expose level; keep the backend default (#2668).
        level: 50,
        permissions: formData.permissionIds,
      },
      {
        onSuccess: () => {
          notify.success(t('rolesManagement.roleCreated'), t('rolesManagement.roleCreatedDesc', { name: formData.name }));
          setCreateDialogOpen(false);
        },
        onError: (error) => {
          notify.error(t('rolesManagement.saveFailed'), describeApiError(error, tc, { fallback: tc('errors.generic') }));
        },
      }
    );
  };

  return (
    <Box>
      <Stack spacing={3}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('rolesManagement.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('rolesManagement.subtitle')}
            </Typography>
          </Box>
          {canManageRoles && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (!hasPermission(MANAGE_ROLES_PERMISSION)) return;
                setCreateDialogOpen(true);
              }}
              disabled={createRole.isPending}
            >
              {t('rolesManagement.createRole')}
            </Button>
          )}
        </Box>

        {/* Roles Grid */}
        <RolesList />

        {/* Create Dialog */}
        <RoleFormDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSave={handleCreateRole}
          saving={createRole.isPending}
        />
      </Stack>
    </Box>
  );
}
